import type { AlgorithmChallenge } from '@code-arcade/engine'

export const countingSort: AlgorithmChallenge = {
  id: 'counting-sort',
  title: 'Counting Sort',
  level: 3,
  chapter: 'chapter-5',
  mode: 'race-it',
  description: 'When keys are bounded integers, skip comparisons entirely — count, then place. O(n + k).',

  realWorld: {
    scenario: "ByteForge processes 5M financial transactions nightly across 5,000 files. Each record has an account tier (1–100) and a timestamp. The ETL pipeline groups by tier, then sorts within each group by date. Current approach: Array.sort() on the full 5M set.",
    consequence: 'O(n log n) on 5M ≈ 112M operations for the grouping pass. Counting sort on account tier 1–100 is O(n + 100) — one pass, no comparisons. 22× faster before the per-group date sort even starts. 4-hour ETL → 11 minutes.',
    products: [],
  },

  visualization: {
    type: 'bar-array',
    defaultSize: 20,
    allowCustomInput: false,
  },

  interrupts: [
    {
      atStep: 30,
      character: 'raj-lohani',
      mood: 'urgent',
      message: "The ETL ran for 4 hours. Board meeting is in 3. What happened?",
    },
  ],

  complexity: { time: 'O(n + k)', space: 'O(k)' },
  xpReward: { oneStar: 200, twoStar: 400, threeStar: 800 },

  keyTakeaway:
    'Comparison sorts have an O(n log n) lower bound because they learn one bit of information per ' +
    'comparison. But if your keys are bounded integers — account tiers, status codes, HTTP status ' +
    'ranges, zip codes, priority levels — you already know every possible value. Counting sort skips ' +
    'comparisons entirely: bucket each record, read the buckets in order. The pattern generalizes ' +
    'anywhere the key space is smaller than the dataset: database index range scans, hash partitioning, ' +
    'consistent hashing in distributed systems. Before reaching for sort(), ask: do I know the range of my keys?',

  whenToUse: [
    'Grouping records by bounded integer keys — account tiers, status codes, priority levels, age buckets',
    'Sorting fixed-width fields: dates as YYYYMMDD integers, IP address octets, HTTP status codes',
    'Frequency histograms — you\'re already building the count array, output it directly',
    'Stable multi-pass sort on composite keys (sort by date within tier = radix sort on two passes)',
    'Radix sort base case — counting sort is the inner loop that makes radix sort O(n)',
  ],

  whenNotToUse: [
    'Keys are not bounded integers — strings, floats, or UUIDs need a comparison sort',
    'Key range k is much larger than n — O(k) space dominates (e.g., sort 100 items by value up to 1M)',
    'Data doesn\'t fit in memory — counting sort needs the full count array in RAM',
  ],

  pseudocode: `procedure countingSort(A, k)   // k = max value in A
  count ← array of k + 1 zeros

  // Phase 1: count
  for each element x in A
    count[x] ← count[x] + 1

  // Phase 2: write back in order
  writePos ← 0
  for v from 0 to k
    repeat count[v] times
      A[writePos] ← v
      writePos ← writePos + 1

  return A`,

  codeSnippets: [
    {
      language: 'csharp',
      label: 'C#',
      code: `public static void CountingSort(int[] arr, int maxVal) {
    int[] count = new int[maxVal + 1];
    foreach (int x in arr) count[x]++;

    int pos = 0;
    for (int v = 0; v <= maxVal; v++)
        while (count[v]-- > 0) arr[pos++] = v;
}

// Counting sort beats Array.Sort() when max value (k) is small relative to n:
// Array.Sort(arr);  // O(n log n) — no knowledge of key range
// CountingSort(arr, 100);  // O(n + 100) when tiers are 1–100`,
    },
    {
      language: 'python',
      label: 'Python',
      code: `def counting_sort(arr: list[int], max_val: int) -> list[int]:
    count = [0] * (max_val + 1)
    for x in arr:
        count[x] += 1
    pos = 0
    for v, c in enumerate(count):
        while c > 0:
            arr[pos] = v
            pos += 1
            c -= 1
    return arr

# When keys are bounded, counting sort beats the built-in:
# arr.sort()                   # O(n log n) TimSort
# counting_sort(arr, 100)      # O(n + 100) when values 1–100`,
    },
    {
      language: 'javascript',
      label: 'JavaScript',
      code: `function countingSort(arr, maxVal) {
  const count = new Array(maxVal + 1).fill(0);
  for (const x of arr) count[x]++;

  let pos = 0;
  for (let v = 0; v <= maxVal; v++)
    while (count[v]-- > 0) arr[pos++] = v;

  return arr;
}

// When keys are bounded, this beats the built-in:
// arr.sort((a, b) => a - b);     // O(n log n) V8 TimSort
// countingSort(arr, 100);         // O(n + 100) for tiers 1–100`,
    },
    {
      language: 'typescript',
      label: 'TypeScript',
      code: `function countingSort(arr: number[], maxVal: number): number[] {
  const count = new Array<number>(maxVal + 1).fill(0);
  for (const x of arr) count[x]++;

  let pos = 0;
  for (let v = 0; v <= maxVal; v++)
    while (count[v]-- > 0) arr[pos++] = v;

  return arr;
}

// When keys are bounded, this beats the built-in:
// arr.sort((a, b) => a - b);     // O(n log n) V8 TimSort
// countingSort(arr, 100);         // O(n + 100) for tiers 1–100`,
    },
    {
      language: 'java',
      label: 'Java',
      code: `public static void countingSort(int[] arr, int maxVal) {
    int[] count = new int[maxVal + 1];
    for (int x : arr) count[x]++;

    int pos = 0;
    for (int v = 0; v <= maxVal; v++)
        while (count[v]-- > 0) arr[pos++] = v;
}

// When keys are bounded, this beats the built-in:
// Arrays.sort(arr);              // O(n log n) dual-pivot quicksort
// countingSort(arr, 100);        // O(n + 100) for tiers 1–100`,
    },
  ],

  sampleDataset: {
    label: 'ByteForge transaction account tiers (1–10 shown; production: 1–100)',
    preview: [3, 7, 1, 9, 3, 5, 2, 8, 3, 6, 1, 9],
    atScale: {
      n: 5000000,
      leftLabel: 'Array.sort()',
      leftNote: '~112M operations',
      rightLabel: 'Counting Sort (k=100)',
      rightNote: '~5M + 100 operations',
    },
  },
}
